import React, { useState } from "react";
import { Plus } from "lucide-react";
import { VIAL_IMAGE } from "../data/products";
import { useCart } from "../context/CartContext";
import { toast } from "sonner";
import { trackAddToCart } from "../lib/analytics";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "./ui/select";

export const ProductCard = ({ product }) => {
    const { addItem } = useCart();
    const hasVariants = Array.isArray(product.variants) && product.variants.length > 0;
    const [variantId, setVariantId] = useState(hasVariants ? product.variants[0].id : null);

    const selectedVariant = hasVariants
        ? product.variants.find((v) => v.id === variantId) || product.variants[0]
        : null;

    // Effective values (variant overrides parent for image/dosage/price)
    const effective = selectedVariant
        ? {
              id: selectedVariant.id,
              name: product.name,
              category: product.category,
              short: product.short,
              dosage: selectedVariant.dosage,
              price: selectedVariant.price,
              regular_price: selectedVariant.regular_price,
              image: selectedVariant.image,
          }
        : product;

    const handleAdd = () => {
        // Cart entry uses the selected variant's id so quantities per
        // strength are tracked independently.
        const payload = {
            id: effective.id,
            name: effective.name,
            category: effective.category,
            dosage: effective.dosage,
            price: effective.price,
            regular_price: effective.regular_price,
            short: effective.short,
            image: effective.image,
        };
        addItem(payload);
        trackAddToCart(payload);
        toast.success(`${effective.name} added to cart`, {
            description: effective.dosage,
        });
    };

    const imageSrc = effective.image || VIAL_IMAGE;
    const showFullDescription = hasVariants || product.fullDescription === true;


    return (
        <article
            className="card-hover group relative rounded-2xl border border-white/10 bg-[#0A0A12]/70 overflow-hidden flex flex-col h-full"
            data-testid={`product-card-${product.id}`}
        >
            <div className="absolute top-3.5 right-3.5 z-10">
                <span className="text-[9px] tracking-[0.18em] uppercase px-2.5 py-1 rounded-full bg-indigo-500/15 text-indigo-300 border border-indigo-500/20">
                    Research Only
                </span>
            </div>

            {/* IMAGE — uniform across all cards */}
            <div className="aspect-square w-full relative overflow-hidden bg-[#070710]">
                <div
                    className="absolute inset-0"
                    style={{
                        background:
                            "radial-gradient(ellipse at 50% 55%, rgba(99,102,241,0.22) 0%, rgba(59,130,246,0.10) 40%, transparent 75%)",
                    }}
                />
                <img
                    src={imageSrc}
                    alt={`${effective.name} ${effective.dosage} — premium research peptide vial by Quantum Genesis Labs`}
                    className="absolute inset-0 w-full h-full object-contain p-5 transition-all duration-500 ease-out group-hover:scale-[1.03]"
                    loading="lazy"
                    onError={(e) => {
                        if (e.target.src !== VIAL_IMAGE) e.target.src = VIAL_IMAGE;
                    }}
                />
                <div className="absolute -bottom-10 left-1/2 -translate-x-1/2 w-3/4 h-12 rounded-full bg-indigo-500/22 blur-2xl" />
            </div>

            {/* BODY */}
            <div className="p-6 flex flex-col flex-1">
                <p className="text-[10px] tracking-[0.22em] uppercase text-indigo-400 mb-2">
                    {effective.category}
                </p>
                <h3 className="text-xl font-medium text-white tracking-tight">{effective.name}</h3>
                <p className="text-sm text-slate-400 mt-1">{effective.dosage}</p>
                <p className={`text-sm mt-3 flex-1 ${showFullDescription ? "text-slate-200/90 leading-[1.55]" : "text-slate-300/75 leading-relaxed line-clamp-2"}`}>
                    {effective.short}
                </p>

                {/* Variant strength selector */}
                {hasVariants && (
                    <div className="mt-5">
                        <label className="text-[10px] tracking-[0.22em] uppercase text-slate-500 mb-2 block">
                            {product.variantLabel || "Select Strength"}
                        </label>
                        <Select value={variantId} onValueChange={setVariantId}>
                            <SelectTrigger
                                data-testid={`variant-select-${product.id}`}
                                className="h-10 rounded-xl bg-white/[0.03] hover:bg-white/[0.06] border border-white/10 hover:border-indigo-400/40 text-sm text-white focus:ring-1 focus:ring-indigo-400/50 transition-all duration-300 ease-out data-[state=open]:border-indigo-400/55 data-[state=open]:bg-white/[0.06] data-[state=open]:shadow-[0_0_24px_-6px_rgba(99,102,241,0.55)] [&>svg]:transition-transform [&>svg]:duration-300 [&>svg]:ease-out data-[state=open]:[&>svg]:rotate-180"
                            >
                                <SelectValue placeholder="Choose strength" />
                            </SelectTrigger>
                            <SelectContent
                                position="popper"
                                sideOffset={8}
                                className="bg-[#0A0A12]/95 backdrop-blur-xl border border-indigo-500/20 text-white shadow-[0_24px_60px_-20px_rgba(99,102,241,0.45)] rounded-xl overflow-hidden duration-300 ease-out data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0 data-[state=open]:zoom-in-95 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-3 data-[side=top]:slide-in-from-bottom-3"
                            >
                                {product.variants.map((v) => (
                                    <SelectItem
                                        key={v.id}
                                        value={v.id}
                                        data-testid={`variant-option-${v.id}`}
                                        className="text-sm rounded-lg my-0.5 cursor-pointer focus:bg-indigo-500/18 focus:text-white data-[state=checked]:text-indigo-200 transition-colors duration-200"
                                    >
                                        <span className="flex items-center justify-between gap-6 w-full">
                                            <span>{v.strength.toLowerCase()}</span>
                                            <span className="text-slate-400 text-xs">${Number(v.price).toFixed(2)}</span>
                                        </span>
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                )}

                <div className="mt-6 flex items-center justify-between gap-3 pt-5 border-t border-white/5">
                    <div className="flex items-baseline gap-2">
                        <span className="text-lg font-medium text-white" data-testid={`product-sale-price-${product.id}`}>
                            ${Number(effective.price).toFixed(2)}
                        </span>
                        {effective.regular_price && effective.regular_price > effective.price && (
                            <span
                                className="text-sm text-slate-400 line-through tracking-tight"
                                data-testid={`product-regular-price-${product.id}`}
                            >
                                ${Number(effective.regular_price).toFixed(2)}
                            </span>
                        )}
                    </div>
                    <button
                        onClick={handleAdd}
                        data-testid={`add-to-cart-${product.id}`}
                        className="btn-glow flex items-center gap-1.5 px-4 py-2 rounded-full bg-indigo-500 hover:bg-indigo-400 text-white text-sm font-medium"
                    >
                        <Plus className="w-4 h-4" /> Add
                    </button>
                </div>
            </div>
        </article>
    );
};

export default ProductCard;
